import { createRouter, createWebHistory } from 'vue-router'
import { setStore, getStore } from '@/util/store'

const basename = ''
const redirectConfig = {
  manager: '/admin',
  teacher: '/home',
  student: '/student',
}

// 每次导航时动态读取当前角色，避免角色切换或初始化时机导致根路径重定向失效（空白页）
const getRoleHomePath = () => {
  const currentRole = getStore({ name: 'currentRole' })
  return redirectConfig[currentRole] || '/noPermission'
}

const routes = [
  {
    path: '/',
    redirect: getRoleHomePath,
  },
  {
    path: '/admin',
    name: 'Admin',
    title: '管理员首页',
    component: () => import(/* webpackChunkName: "home" */ '@/views/Index/Admin/index.vue'),
  },
  {
    path: '/home',
    component: () => import(/* webpackChunkName: "center" */ '@/views/Index/Teacher/index.vue'),
    title: '教师首页',
    redirect: '/bei',
    children: [
      {
        path: '/bei',
        title: '备',
        name: 'Bei',
        component: () => import(/* webpackChunkName: "center" */ '@/views/Index/Teacher/Bei/index.vue'),
      },
      {
        path: '/jiao',
        title: '教',
        name: 'Jiao',
        component: () => import(/* webpackChunkName: "work" */ '@/views/Index/Teacher/Jiao/index.vue'),
      },
      {
        path: '/ping',
        title: '评',
        name: 'Ping',
        component: () => import(/* webpackChunkName: "work" */ '@/views/Index/Teacher/Ping/index.vue'),
      },
      {
        path: '/gai',
        title: '改',
        name: 'Gai',
        component: () => import(/* webpackChunkName: "work" */ '@/views/Index/Teacher/Gai/index.vue'),
      },
      {
        path: '/fu',
        title: '辅',
        name: 'Fu',
        component: () => import(/* webpackChunkName: "work" */ '@/views/Index/Teacher/Fu/index.vue'),
      },
      {
        path: '/guan',
        title: '管',
        name: 'Guan',
        component: () => import(/* webpackChunkName: "work" */ '@/views/Index/Teacher/Guan/index.vue'),
      },
    ],
  },
  {
    path: '/student',
    name: 'Student',
    title: '学生首页',
    component: () => import(/* webpackChunkName: "home" */ '@/views/Index/Student/index.vue'),
  },
  {
    path: '/notice',
    name: 'notice',
    component: () => import(/* webpackChunkName: "main" */ '@/views/Notice.vue'),
  },
  {
    path: '/courseDetail',
    name: 'courseDetail',
    component: () => import(/* webpackChunkName: "main" */ '@/views/sourcePreview/courseDetail/index.vue'),
  },
  {
    path: '/paperPreview',
    name: 'paperPreview',
    component: () =>
      import(/* webpackChunkName: "main" */ '@/views/sourcePreview/paperDetail/previewPractice/index.vue'),
  },
  {
    path: '/curriculumDetail',
    name: 'curriculumDetail',
    component: () => import(/* webpackChunkName: "main" */ '@/views/sourcePreview/curriculumDetail/index.vue'),
  },
  {
    path: '/newCurriculumDetail',
    name: 'newCurriculumDetail',
    component: () => import(/* webpackChunkName: "main" */ '@/views/sourcePreview/newCurriculumDetail/index.vue'),
  },
  {
    path: '/resourceDetail',
    name: 'resourceDetail',
    component: () => import(/* webpackChunkName: "main" */ '@/views/sourcePreview/resourceDetail/index.vue'),
  },
  {
    path: '/practiceDetail',
    name: 'practiceDetail',
    component: () => import(/* webpackChunkName: "main" */ '@/views/sourcePreview/practiceDetail/index.vue'),
  },
  {
    path: '/articleDetail',
    name: 'articleDetail',
    component: () => import(/* webpackChunkName: "main" */ '@/views/sourcePreview/articleDetail/index.vue'),
  },
  {
    path: '/newsDetail',
    name: 'newsDetail',
    component: () => import(/* webpackChunkName: "main" */ '@/views/sourcePreview/newsDetail/index.vue'),
  },
  {
    path: '/generate-paper', // 特殊标识学科网落地页
    name: 'generate-paper',
    component: () => import(/* webpackChunkName: "main" */ '@/views/xkw/index.vue'),
  },
  {
    path: '/accountInfo',
    name: 'accountInfo',
    component: () => import(/* webpackChunkName: "main" */ '@/views/AccountInfo.vue'),
  },
  {
    path: '/userInfo',
    name: 'userInfo',
    component: () => import(/* webpackChunkName: "main" */ '@/views/UserInfo.vue'),
  },
  {
    path: '/spaceInfo',
    name: 'spaceInfo',
    component: () => import(/* webpackChunkName: "main" */ '@/views/SpaceInfo.vue'),
  },
  {
    path: '/stuSpaceInfo',
    name: 'stuSpaceInfo',
    component: () => import(/* webpackChunkName: "main" */ '@/views/stuSpaceInfo/index.vue'),
    props: true,
  },
  {
    path: '/boxiaoShareFrame',
    name: 'boxiaoShareFrame',
    component: () => import(/* webpackChunkName: "main" */ '@/views/boxiaoShare.vue'),
  },
  {
    path: '/noPermission',
    name: 'noPermission',
    component: () => import(/* webpackChunkName: "main" */ '@/views/NoPermission.vue'),
  },
]

export const router = createRouter({
  history: createWebHistory(basename),
  routes,
})

router.beforeEach((to, from, next) => {
  const currentRole = getStore({ name: 'currentRole' })
  // 定义每个角色允许访问的首页路由
  const rolePermissions = {
    manager: ['/admin'],
    teacher: ['/home', '/bei', '/jiao', '/ping', '/gai', '/fu', '/guan'],
    student: ['/student'],
    guest: ['/noPermission'], // 无角色时的默认页面
  }

  const hasPermission = rolePermissions[currentRole]?.some((path) => to.path.startsWith(path))

  const topLevelRoutes = ['/admin', '/home', '/bei', '/jiao', '/ping', '/gai', '/fu', '/guan', '/student']

  if (topLevelRoutes.includes(to.path)) {
    if (hasPermission) {
      next() // 允许访问
    } else {
      // 根据角色重定向到默认页面
      if (currentRole === 'teacher') {
        next('/bei') // 教师默认重定向到 /bei
      } else {
        next(redirectConfig[currentRole] || '/noPermission')
      }
    }
  } else {
    next() // 对其他页面不进行权限校验，直接允许访问
  }
})

export function addRoutes(list) {
  const addAppsList = list
    .filter((val) => val.type === 1)
    .map((item) => {
      return {
        path: item.projectRouteKey,
        name: item.projectName,
        component: () => {
          return import(/* webpackChunkName: "apps" */ `@/views/Application.vue`)
        },
      }
    })
  const addBoxiaoList = list
    .filter((val) => val.type === 2)
    .map((item) => {
      return {
        path: item.projectRouteKey,
        name: item.projectName,
        component: () => {
          return import(/* webpackChunkName: "boxiao" */ `@/views/boxiao.vue`)
        },
      }
    })
  addAppsList.length &&
    addAppsList.forEach((item) => {
      router.addRoute(item)
    })
  addBoxiaoList.length &&
    addBoxiaoList.forEach((item) => {
      router.addRoute(item)
    })
  setStore({
    name: 'platformApps',
    content: list,
    type: 'session',
  })
}
